import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";

const title = "Årskurser 3–18 år | Swedish International School";
const description =
  "Förskola, förskoleklass och grundskola årskurs 1–6 på plats, samt högstadium via Sofia Distans och gymnasium via Hermods med handledning i Estepona.";

export const Route = createFileRoute("/arskurser")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: absoluteUrl("/arskurser") },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/arskurser"),
  }),
  component: Arskurser,
});

const levels = [
  {
    name: "Förskola",
    age: "3–5 år",
    text: "Lekfull start med svenska som grundspråk och tidig introduktion till spanska och engelska.",
  },
  {
    name: "Förskoleklass",
    age: "5–6 år",
    text: "Övergången till skolan med fokus på språk, socialt samspel och trygghet i gruppen.",
  },
  {
    name: "Grundskola",
    age: "Årskurs 1–6",
    text: "Svensk läroplan med legitimerade lärare, skriftliga omdömen och utökad spanska på schemat.",
  },
  {
    name: "Högstadium",
    age: "Årskurs 7–9",
    text: "Studier via Sofia Distans med handledare på plats i skolans lokaler.",
  },
  {
    name: "Gymnasium",
    age: "År 1–3",
    text: "Studier via Hermods med handledning hos oss, i kombination med idrottsprofil om du vill.",
  },
  {
    name: "Fritids",
    age: "Efter skoltid",
    text: "Fritidsverksamhet i anslutning till skolan, med rörelse och aktiviteter utomhus.",
  },
];

function Arskurser() {
  return (
    <>
      <PageHero
        eyebrow="Årskurser"
        title="Från förskola till gymnasiestudier"
        intro="Swedish International School tar emot elever från förskola till årskurs 6 på plats, samt högstadie- och gymnasieelever på distans med handledning. Målet är undervisning på svenska med utökad spanska och engelska."
      />

      <section className="mx-auto max-w-6xl px-5 py-16">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {levels.map((l) => (
            <article key={l.name} className="rounded-xl border border-border bg-card p-7 shadow-soft">
              <p className="text-xs font-semibold tracking-[0.18em] text-primary uppercase">{l.age}</p>
              <h2 className="mt-3 text-xl font-semibold">{l.name}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{l.text}</p>
            </article>
          ))}
        </div>
      </section>


      <Prose>
        <h2>Så är stadierna uppbyggda</h2>
        <p>
          Skolan följer den svenska läroplanen och arbetar i åldersnära grupper. I förskolan ligger
          tyngdpunkten på lek, språk och trygghet, i förskoleklassen förbereds barnen för skolarbetet
          och från årskurs 1 följer undervisningen kursplanernas centrala innehåll i varje ämne.
          Eftersom skolan är liten arbetar lärarna nära varandra och känner alla elever, vilket gör
          det enklare att anpassa tempo och innehåll efter den enskilda eleven.
        </p>
        <p>
          Från högstadiet läser eleverna via Sofia Distans och på gymnasienivå via Hermods. Eleverna
          är på plats i skolans lokaler under skoldagen och har handledare som hjälper till med
          planering, studieteknik och uppföljning. Betyg och kursintyg utfärdas av respektive
          distansskola.
        </p>

        <h2>Språk genom hela skolgången</h2>
        <p>
          Undervisningsspråket är svenska. Spanska finns med på schemat från förskolan och fortsätter
          genom hela grundskolan, och engelska introduceras tidigt och byggs på i takt med
          läroplanen. Många av våra elever lever i en flerspråkig vardag, och målet är att de ska
          kunna byta språk med självförtroende – i klassrummet, på skolgården och utanför skolan.
        </p>

        <h2>Övergångar mellan stadier</h2>
        <p>
          Övergångar planeras tillsammans med vårdnadshavare. Inför att ett barn börjar hos oss går
          vi igenom tidigare skolgång, eventuella behov och var i kursplanen eleven befinner sig, så
          att starten blir så mjuk som möjligt. Det går bra att börja mitt i en termin; kontakta oss
          så tittar vi på lediga platser i aktuell årskurs.
        </p>
        <p>
          Är ni osäkra på vilken årskurs som passar utgår vi från elevens födelseår enligt svensk
          modell, men gör alltid en individuell bedömning tillsammans med er. I{" "}
          <a className="text-primary link-quiet" href="/ansokan">ansökan</a> föreslås årskurs
          automatiskt utifrån födelsedatum, och ni kan ändra förslaget om ni vill.
        </p>

        <h2>Vad som ingår i skoldagen</h2>
        <p>
          Skoldagen innehåller lunch på skolan, utevistelse och rörelse. Efter skoldagen finns{" "}
          <a className="text-primary link-quiet" href="/fritidsverksamhet">fritids</a>, och för
          den som vill finns idrottsprofilerna padel och tennis samt fotbollsakademin som tillval.
          Avgifter och vad som ingår per stadium hittar ni på{" "}
          <a className="text-primary link-quiet" href="/avgifter">avgiftssidan</a>.
        </p>
      </Prose>

      <FaqBlock items={faqFor("arskurser", "sv")} locale={"sv"} />

      <CtaBand
        title="Osäker på vilken årskurs som passar?"
        text="Kontakta oss så går vi igenom upplägget tillsammans med er familj."
      />
      <div className="h-20" />
    </>
  );
}
